import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  Link,
  ToastUtils,
  Typography,
} from '@forgeglobal/design-framework';
import DetailCard, {
  DetailCardItem,
} from '@tcs/common/components/molecules/DetailCard';
import formatDate from '@tcs/common/utils/formatDate';
import { EsignConsentPage } from '@tcs/client-portal-common/components/molecules/OnlineAccessSelector/EsignConsentPage';
import useEsignConsentStatus from '@tcs/client-portal-common/hooks/useEsignConsentStatus';
import { useEsignConsentStatusMutation } from '@tcs/client-portal-common/hooks/useEsignConsentStatusMutation';
import { EsignConsentStatus } from '@tcs/client-portal-api/platform_generated';
import { useStyles } from './style';

type TermsCardProps = {
  accountNumber: string;
};

// Displays all terms & agreements the client has signed.
const TermsCard = ({ accountNumber }: TermsCardProps) => {
  const classes = useStyles();
  const { loading, data: esignConsentModel } =
    useEsignConsentStatus(accountNumber);
  const mutator = useEsignConsentStatusMutation(accountNumber);
  const [open, setOpen] = useState(false);

  if (!esignConsentModel) {
    return null;
  }

  const esignStatusText = (() => {
    switch (esignConsentModel.esignConsentStatus) {
      case 'Unset':
        return 'Not Set';
      case 'Allow':
        return `Accepted on ${formatDate(esignConsentModel.esignConsentDate)}`;
      case 'Deny':
        return `Declined on ${formatDate(esignConsentModel.esignConsentDate)}`;
    }
  })();

  const esignStatusDisclaimer = (() => {
    switch (esignConsentModel.esignConsentStatus) {
      case 'Unset':
        return '';
      case 'Allow':
        return 'Important account documents, including statements, tax forms, and account disclosures, will be delivered to you electronically.';
      case 'Deny':
        return (
          <div>
            Important account documents, including statements, tax forms, and
            account disclosures, will be mailed to your address of record.
            Additional fees may apply for electing to receive paper documents.
            Please refer to our current{' '}
            <Link
              href="https://forgetrust.com/forms/fee-schedule-and-financial-disclosure/"
              target="_blank"
              data-test-id={`esign-doc-link`}>
              fee schedule
            </Link>{' '}
            for further information.
          </div>
        );
    }
  })();

  const handleEsignClick = async (value: EsignConsentStatus) => {
    try {
      await mutator.mutateAsync(value);
      ToastUtils.success(`Settings changes saved.`, true);
    } catch (error) {
      ToastUtils.error(
        `Failed to save settings. If errors persist, contact support.`,
        true
      );
    }
    setOpen(false);
  };

  const saveButton = (
    <Button type="button" color="secondary" onClick={() => setOpen(true)}>
      Change Settings
    </Button>
  );

  return (
    <DetailCard
      title="Communication Settings"
      action={saveButton}
      loading={loading || mutator.isLoading}>
      <DetailCardItem
        label="Electronic Document Delivery:"
        value={`${esignStatusText}`}
      />

      <div className={classes.disclaimer}>
        <Typography variant="caption" color="textSecondary" align="left">
          {esignStatusDisclaimer}
        </Typography>
      </div>

      <Dialog open={open} disableEnforceFocus>
        <DialogContent>
          {
            <EsignConsentPage
              handleClick={handleEsignClick}
              hasScrolled={true}
            />
          }
        </DialogContent>
      </Dialog>
    </DetailCard>
  );
};

export default TermsCard;
